/**
 * Gated HITL bridge for `bring_prior_contour` — builds a COMPARISON
 * structure set on the current scan holding the current target structure
 * and the earlier scan's one ("<name>_prior"), using only existing viewer
 * actions (see the engine adapter). The original structure sets are not
 * changed.
 *
 * Checked before the card: the active structure set and the current
 * target; the earlier structure set is on a DIFFERENT scan; both are
 * target structures (GTV / CTV / PTV) of the SAME type; the earlier one
 * has contours; and the scans share a frame of reference or have an
 * APPROVED registration (aligned by hand — never aligned by the agent).
 * Apply re-checks the alignment. Pure/React-free; ports injected.
 */
import {
    asRecord,
    failedPlan,
    mutatingPreamble,
    type MutatingPlan,
    type MutatingToolCall,
    type PendingFlag,
} from '../core';
import { gatedChange, type ChangeProposal } from '../core/changeProposal';
import type { StructureChangeOutcome } from '../../contract';
import {
    daysBetween,
    TARGET_TYPES,
    type RegistrationState,
    type Timepoint,
} from './timepoints';

export type BringPriorProposal = ChangeProposal<StructureChangeOutcome>;

export interface BuildComparisonArgs {
    readonly priorSetUid: string;
    readonly priorRoiNumber: number;
    readonly currentRoiNumber: number;
    /** Name for the earlier structure's copy, e.g. "GTV_prior". */
    readonly priorCopyName: string;
}

export interface BringPriorDeps extends PendingFlag {
    timepoints(): ReadonlyArray<Timepoint> | undefined;
    /** Identity of the active structure set, read live. */
    currentContextId(): string | undefined;
    /** The existing viewer steps; resolves to the new set's label. */
    buildComparisonSet(
        args: BuildComparisonArgs
    ): Promise<{ readonly setLabel: string }>;
}

const MAX_NAME = 16;
const SUFFIX = '_prior';

/** "<name>_prior", shortened to the 16-character name limit. */
export function priorCopyName(name: string): string {
    return `${name.slice(0, MAX_NAME - SUFFIX.length)}${SUFFIX}`;
}

const ALIGNMENT_NEEDED: Partial<Record<RegistrationState, string>> = {
    unapproved:
        'the registration between the scans is not approved; review and ' +
        'approve it on the Registration step first',
    none:
        'the scans are not aligned; create and approve a registration on ' +
        'the Registration step first (aligning is done by hand)',
};

const byName = <T extends { name: string }>(
    items: ReadonlyArray<T>,
    name: string
) =>
    items.find((i) => i.name === name) ??
    items.find((i) => i.name.toLowerCase() === name.toLowerCase());

export async function planBringPriorContour(
    call: MutatingToolCall,
    deps: BringPriorDeps
): Promise<MutatingPlan<BringPriorProposal>> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const o = asRecord(call.input);
    const priorSetUid = o?.priorStructureSetUid;
    if (
        typeof priorSetUid !== 'string' ||
        typeof o?.priorRoi !== 'string' ||
        typeof o?.currentRoi !== 'string'
    ) {
        return failedPlan(call.toolCallId, 'invalid bring_prior_contour input');
    }
    const tps = deps.timepoints();
    if (!tps) {
        return failedPlan(call.toolCallId, 'no patient is loaded');
    }
    const current = tps.find((t) => t.isCurrent);
    const activeSet = current?.structureSets.find((s) => s.isActive);
    if (!current || !activeSet) {
        return failedPlan(
            call.toolCallId,
            'no structure set is active on the current scan'
        );
    }
    const prior = tps.find((t) =>
        t.structureSets.some((s) => s.uid === priorSetUid)
    );
    const priorSet = prior?.structureSets.find((s) => s.uid === priorSetUid);
    if (!prior || !priorSet) {
        return failedPlan(
            call.toolCallId,
            'that earlier structure set was not found; call list_timepoints'
        );
    }
    if (prior.isCurrent) {
        return failedPlan(
            call.toolCallId,
            'that structure set is on the current scan; choose one from an ' +
                'earlier scan'
        );
    }
    const onlyTargets = `only target structures (${TARGET_TYPES.join(', ')}) can be compared`;
    const currentRoi = byName(activeSet.targets, o.currentRoi);
    if (!currentRoi) {
        return failedPlan(
            call.toolCallId,
            `'${o.currentRoi}' is not a target structure in the active ` +
                `structure set; ${onlyTargets}`
        );
    }
    const priorRoi = byName(priorSet.targets, o.priorRoi);
    if (!priorRoi) {
        return failedPlan(
            call.toolCallId,
            `'${o.priorRoi}' is not a target structure in '${priorSet.label}'; ` +
                onlyTargets
        );
    }
    if (priorRoi.type !== currentRoi.type) {
        return failedPlan(
            call.toolCallId,
            `'${priorRoi.name}' is ${priorRoi.type} but '${currentRoi.name}' ` +
                `is ${currentRoi.type}; compare structures of the same type`
        );
    }
    if (priorRoi.contourCount === 0) {
        return failedPlan(
            call.toolCallId,
            `'${priorRoi.name}' has no contours on the earlier scan`
        );
    }
    const alignment = ALIGNMENT_NEEDED[prior.registration];
    if (alignment) {
        return failedPlan(call.toolCallId, alignment);
    }
    const copyName = priorCopyName(priorRoi.name);
    if (copyName.toLowerCase() === currentRoi.name.toLowerCase()) {
        return failedPlan(
            call.toolCallId,
            `the current structure is already named '${copyName}'`
        );
    }

    const days = daysBetween(prior.date, current.date);
    const when = (d: string) => d || 'unknown date';
    const interval =
        days === undefined
            ? 'scan interval unknown (missing scan date)'
            : `${days} day${Math.abs(days) === 1 ? '' : 's'} between the scans`;
    const alignedBy =
        prior.registration === 'same-frame'
            ? 'The scans share a frame of reference (no registration needed).'
            : 'Aligned with the approved registration between the scans.';

    return {
        kind: 'proposal',
        toolCallId: call.toolCallId,
        proposal: gatedChange<StructureChangeOutcome>({
            toolCallId: call.toolCallId,
            title: 'Compare with earlier scan',
            summary:
                `bring “${priorRoi.name}” from ${when(prior.date)} onto this ` +
                `scan (${when(current.date)}) next to “${currentRoi.name}”`,
            details: [
                interval.charAt(0).toUpperCase() + interval.slice(1) + '.',
                alignedBy,
                `A new structure set is created with “${copyName}” (earlier) ` +
                    `and “${currentRoi.name}” (current), and the viewer ` +
                    'switches to it. The original structure sets are not ' +
                    'changed.',
                'The earlier contour is resampled onto this scan.',
            ],
            undoable: false,
            currentContextId: deps.currentContextId,
            recheck: () => {
                const now = deps
                    .timepoints()
                    ?.find((t) =>
                        t.structureSets.some((s) => s.uid === priorSetUid)
                    );
                return now
                    ? ALIGNMENT_NEEDED[now.registration]
                    : 'the earlier scan is gone';
            },
            run: async () => {
                const { setLabel } = await deps.buildComparisonSet({
                    priorSetUid,
                    priorRoiNumber: priorRoi.roiNumber,
                    currentRoiNumber: currentRoi.roiNumber,
                    priorCopyName: copyName,
                });
                return {
                    status: 'applied',
                    roi: copyName,
                    summary:
                        `created the comparison structure set '${setLabel}' ` +
                        `with '${currentRoi.name}' (current scan, ` +
                        `${when(current.date)}) and '${copyName}' (earlier ` +
                        `scan, ${when(prior.date)}); ${interval}; the viewer ` +
                        'now shows this set',
                };
            },
        }),
    };
}
